/**
 * Identidade de movimento da UCC.
 *
 * Arquétipo: "Energético com contenção premium". A UCC é competição — o
 * movimento precisa ter energia — mas é também dinheiro, chaveamento e regra,
 * então nada quica nem exagera. Uma curva assinatura, três durações. Tudo que
 * animar no produto sai daqui, e é isso que faz interfaces diferentes parecerem
 * o mesmo produto.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export const EASE = {
  /** 80% das animações. Sai rápido, chega macio — expo.out. */
  signature: 'expo.out',
  /** Saídas: acelera para fora do campo de visão. */
  exit: 'power3.in',
  /** Trocas de estado que precisam de simetria (abrir/fechar). */
  state: 'power3.inOut',
} as const;

export const DURATION = {
  quick: 0.18,
  standard: 0.42,
  hero: 0.9,
} as const;

/**
 * Respeita a preferência do sistema. Chamado uma vez no boot: com movimento
 * reduzido, o GSAP passa a executar tudo instantaneamente em vez de nós
 * escrevermos dois caminhos de código para cada animação.
 */
export function applyMotionPreference(): void {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sync = () => {
    gsap.defaults({ duration: reduced.matches ? 0 : DURATION.standard, ease: EASE.signature });
    ScrollTrigger.config({ ignoreMobileResize: true });
  };
  sync();
  reduced.addEventListener('change', sync);
}

/**
 * Entrada padrão: o elemento sobe por trás de uma máscara, como uma placa de
 * estádio subindo. Usada em títulos, cards e linhas de tabela.
 *
 * @param stagger intervalo entre elementos. A regra de 1/3: no máximo um terço
 * dos elementos da cena se movendo ao mesmo tempo — por isso o stagger curto.
 */
export function revealUp(
  targets: gsap.TweenTarget,
  { delay = 0, stagger = 0.06, distance = 28 } = {},
): gsap.core.Tween {
  return gsap.from(targets, {
    yPercent: 0,
    y: distance,
    autoAlpha: 0,
    duration: DURATION.hero,
    ease: EASE.signature,
    stagger,
    delay,
  });
}

/** Contagem numérica — placar, vagas restantes, premiação. */
export function countUp(
  element: HTMLElement,
  to: number,
  { duration = 1.1, format = (v: number) => String(Math.round(v)) } = {},
): gsap.core.Tween {
  const state = { value: 0 };
  return gsap.to(state, {
    value: to,
    duration,
    ease: 'power2.out',
    onUpdate: () => {
      element.textContent = format(state.value);
    },
  });
}

export { gsap, ScrollTrigger, useGSAP };
